import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Users, Globe, ArrowUpDown, ChevronLeft, ChevronRight, Star, Mail, Phone, MapPin, Calendar, Hash, ArrowLeft, X, Tag, FileText, Printer, Download, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactsReferentielPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("last_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const limit = 30;

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/contacts`, {
        headers,
        params: { search, sort_by: sortBy, sort_order: sortOrder, skip: page * limit, limit }
      });
      setContacts(res.data.contacts);
      setTotal(res.data.total);
    } catch { toast.error("Erreur chargement contacts"); }
    finally { setLoading(false); }
  }, [search, sortBy, sortOrder, page]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/api/contacts/stats/summary`, { headers });
      setStats(res.data);
    } catch {}
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams({ search, sort_by: sortBy, sort_order: sortOrder });
      const res = await axios.get(`${API}/api/contacts/export/csv?${params.toString()}`, {
        headers, responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contacts_glotech_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV telecharge");
    } catch { toast.error("Erreur export"); }
  };

  const recalculateTags = async () => {
    try {
      const res = await axios.post(`${API}/api/contacts/auto-tag-all`, {}, { headers });
      toast.success(`${res.data.tagged} contacts re-etiquetes`);
      fetchContacts();
      fetchStats();
    } catch { toast.error("Erreur recalcul tags"); }
  };

  useEffect(() => { fetchContacts(); }, [fetchContacts]);
  useEffect(() => { fetchStats(); }, []);

  const openContact = async (contactId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/api/contacts/${contactId}`, { headers });
      setSelectedContact(res.data);
    } catch { toast.error("Erreur chargement fiche"); }
    finally { setDetailLoading(false); }
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortOrder("asc"); }
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);

  const statusBadge = (s) => {
    const map = {
      checked_in: { label: "Present", cls: "bg-emerald-500/20 text-emerald-400" },
      registered: { label: "Inscrit", cls: "bg-cyan-500/20 text-cyan-400" },
      cancelled: { label: "Annule", cls: "bg-red-500/20 text-red-400" },
      waitlisted: { label: "Attente", cls: "bg-amber-500/20 text-amber-400" },
    };
    const m = map[s] || { label: s, cls: "bg-gray-500/20 text-gray-400" };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${m.cls}`}>{m.label}</span>;
  };

  // ===== CONTACT DETAIL VIEW =====
  if (selectedContact) {
    const c = selectedContact;
    return (
      <div className="min-h-screen bg-[#0B0F19] text-white p-6" data-testid="contact-detail-view">
        <button onClick={() => setSelectedContact(null)} className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors" data-testid="back-to-list">
          <ArrowLeft className="h-4 w-4" /> Retour a la liste
        </button>

        {/* Contact Header */}
        <div className="bg-[#131B2E] border border-[#2A3441] rounded-xl p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold" data-testid="contact-fullname">{c.last_name} {c.first_name}</h1>
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-400">
                {c.email && <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-cyan-400" />{c.email}</span>}
                {c.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-cyan-400" />{c.phone}</span>}
                {(c.city || c.country) && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-cyan-400" />{[c.city, c.country].filter(Boolean).join(", ")}</span>}
                {c.organization && <span className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-cyan-400" />{c.organization}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="bg-[#0099CC]/20 text-[#0099CC] rounded-xl px-4 py-2 text-center">
                <div className="text-2xl font-bold" data-testid="contact-event-count">{c.total_registrations || 0}</div>
                <div className="text-[10px] uppercase tracking-wider">inscriptions</div>
              </div>
            </div>
          </div>
          {c.tags && c.tags.length > 0 && (
            <div className="flex gap-2 mt-4">
              {c.tags.map((t, i) => (
                <span key={i} className="bg-purple-500/20 text-purple-300 text-xs px-2 py-0.5 rounded-full flex items-center gap-1"><Tag className="h-3 w-3" />{t}</span>
              ))}
            </div>
          )}
          {c.notes && <p className="mt-3 text-sm text-gray-400 bg-[#0B0F19] rounded-lg p-3"><FileText className="h-3.5 w-3.5 inline mr-1.5 text-gray-500" />{c.notes}</p>}
          <div className="text-[10px] text-gray-600 mt-3">Contact cree le {new Date(c.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</div>
        </div>

        {/* Registrations History */}
        <div className="bg-[#131B2E] border border-[#2A3441] rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><Calendar className="h-5 w-5 text-[#0099CC]" /> Historique des evenements</h2>
          {c.registrations && c.registrations.length > 0 ? (
            <div className="space-y-3">
              {c.registrations.map((r, i) => (
                <div key={i} className="flex items-center justify-between bg-[#0B0F19] rounded-lg p-4 hover:bg-[#0D1222] transition-colors" data-testid={`registration-${i}`}>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{r.event_title}</div>
                    <div className="text-xs text-gray-500 mt-1 flex gap-3">
                      {r.event_date && <span>{new Date(r.event_date).toLocaleDateString("fr-FR")}</span>}
                      {r.event_location && <span>{r.event_location}</span>}
                      <span className="text-gray-600">Ref: {r.reference}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {r.category && <span className="text-xs text-gray-500">{r.category}</span>}
                    {statusBadge(r.status)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Aucun historique d'evenement lie a ce contact.</p>
          )}
        </div>
      </div>
    );
  }

  // ===== MAIN LIST VIEW =====
  return (
    <div className="min-h-screen bg-[#0B0F19] text-white p-6" data-testid="contacts-referentiel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0099CC]/20 flex items-center justify-center"><Users className="h-5 w-5 text-[#0099CC]" /></div>
            Referentiel Central des Contacts
          </h1>
          <p className="text-gray-500 text-sm mt-1">Base centralisee de tous vos participants — 1 fiche = 1 personne</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={recalculateTags} className="border-[#2A3441] text-gray-400 hover:text-white" data-testid="recalculate-tags-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Tags auto
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} className="border-[#2A3441] text-gray-400 hover:text-white" data-testid="export-csv-btn">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Exporter CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="contacts-stats">
          <div className="bg-[#131B2E] border border-[#2A3441] rounded-xl p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wider">Total contacts</div>
            <div className="text-3xl font-bold text-white mt-1" data-testid="stat-total">{stats.total_contacts}</div>
          </div>
          <div className="bg-[#131B2E] border border-[#2A3441] rounded-xl p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1"><Star className="h-3 w-3 text-amber-400" /> Fideles (2+ events)</div>
            <div className="text-3xl font-bold text-amber-400 mt-1" data-testid="stat-fideles">{stats.fideles}</div>
          </div>
          <div className="bg-[#131B2E] border border-[#2A3441] rounded-xl p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wider">Top pays</div>
            <div className="flex flex-wrap gap-2 mt-2">
              {stats.top_countries?.slice(0, 4).map((c, i) => (
                <span key={i} className="text-xs bg-[#0B0F19] rounded-lg px-2 py-1 text-gray-300">{c.country} <span className="text-[#0099CC] font-bold">{c.count}</span></span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search + Sort */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            data-testid="contacts-search"
            placeholder="Rechercher par nom, email, telephone, ville..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            className="pl-10 bg-[#131B2E] border-[#2A3441] text-white"
          />
        </div>
        <div className="flex gap-2">
          {[
            { key: "last_name", label: "Nom" },
            { key: "total_registrations", label: "Inscriptions" },
            { key: "created_at", label: "Date" },
          ].map(s => (
            <Button
              key={s.key}
              variant={sortBy === s.key ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort(s.key)}
              className={sortBy === s.key ? "bg-[#0099CC] text-white" : "border-[#2A3441] text-gray-400 hover:text-white"}
              data-testid={`sort-${s.key}`}
            >
              {s.label} {sortBy === s.key && (sortOrder === "asc" ? "↑" : "↓")}
            </Button>
          ))}
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-[#131B2E] border border-[#2A3441] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Chargement...</div>
        ) : contacts.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {search ? "Aucun contact ne correspond a cette recherche." : "Aucun contact dans le referentiel."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="contacts-table">
              <thead>
                <tr className="border-b border-[#2A3441] text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Nom</th>
                  <th className="px-4 py-3 hidden md:table-cell">Email</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Telephone</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Localisation</th>
                  <th className="px-4 py-3 text-center">Events</th>
                  <th className="px-4 py-3 hidden md:table-cell">Depuis</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(c => (
                  <tr
                    key={c.id}
                    className="border-b border-[#1A2332] hover:bg-[#0B0F19]/50 cursor-pointer transition-colors"
                    onClick={() => openContact(c.id)}
                    data-testid={`contact-row-${c.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm">{c.last_name} {c.first_name}</div>
                      <div className="text-xs text-gray-500 md:hidden">{c.email}</div>
                      {c.tags?.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {c.tags.map((t, i) => (
                            <span key={i} className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${t === "VIP" ? "bg-amber-500/20 text-amber-400" : t === "Fidele" ? "bg-purple-500/20 text-purple-300" : "bg-cyan-500/20 text-cyan-400"}`}>{t}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400 hidden md:table-cell">{c.email || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-400 hidden lg:table-cell">{c.phone || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-400 hidden lg:table-cell">{[c.city, c.country].filter(Boolean).join(", ") || "-"}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${c.total_registrations >= 2 ? "bg-amber-500/20 text-amber-400" : "bg-gray-700/30 text-gray-400"}`}>
                        {c.total_registrations || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">{c.created_at ? new Date(c.created_at).toLocaleDateString("fr-FR") : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#2A3441]">
            <span className="text-xs text-gray-500">{total} contacts — Page {page + 1}/{totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="border-[#2A3441] text-gray-400" data-testid="prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="border-[#2A3441] text-gray-400" data-testid="next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
